import type { ReactNode } from "react";
import { Link } from "react-router";

/**
 * A destination the workflow lists before it answers.
 *
 * With somewhere to go it is a link with an address. Without, it keeps the link
 * role but carries no address, since the address is the promise a link makes.
 * Staying out of the tab order is the part that matters most: a focus stop
 * leading nowhere costs a keyboard reader more than a missing one, and that is
 * what the step link used to do.
 *
 * The rail, the tab strip and the step link each decided this differently, so
 * what a screen reader reported depended on which stage the attorney was in.
 * The classes stay the caller's, given once for both states, so the unreachable
 * one cannot drift from the live one.
 */
export function MaybeLink({
  to,
  current = false,
  className,
  children,
}: {
  /** Where it goes, or nothing while the destination does not answer. */
  to: string | undefined;
  /** Whether this is the page being read. */
  current?: boolean;
  className?: string;
  children: ReactNode;
}) {
  if (to === undefined) {
    // The role is what carries `aria-disabled`, which is not a global attribute:
    // on a bare span it is not exposed at all. It adds no tab stop.
    return (
      <span role="link" aria-disabled="true" className={className}>
        {children}
      </span>
    );
  }
  return (
    <Link
      to={to}
      aria-current={current ? "page" : undefined}
      className={className}
    >
      {children}
    </Link>
  );
}
